/**
 * Mandar el remito por WhatsApp.
 *
 * Diseño completo: docs/specs/2026-08-22-remito-por-whatsapp.md
 *
 * Todo lo de este archivo son funciones PURAS a proposito: el armado del
 * mensaje y la normalizacion del telefono son donde esto se rompe en la vida
 * real, y una funcion pura se puede probar con una tabla de casos.
 */
import { formatear, type Moneda } from "@tora/shared";

// ─────────────────────────────────────────────────────────────────────────────
// El telefono argentino
// ─────────────────────────────────────────────────────────────────────────────
//
// `normalizarTelefonoAr` y `linkWhatsapp` viven en `@tora/shared`: el backend
// las usa para redirigir al chat del cliente desde la pagina del remito, y la
// heuristica del 15 tiene que ser UNA. Se re-exportan para que este archivo
// siga siendo el punto de entrada de todo lo del remito.

export { normalizarTelefonoAr, linkWhatsapp, type TelefonoNormalizado } from "@tora/shared";

// ─────────────────────────────────────────────────────────────────────────────
// El mensaje
// ─────────────────────────────────────────────────────────────────────────────

export interface ItemRemito {
  descripcion: string;
  cantidad: number;
  importe: number;
  /**
   * Se vendio a pedido y todavia no se entrego. Sin esto el remito decia
   * «Pagado» y nada mas, como si el cliente se hubiera llevado el equipo.
   */
  a_pedido?: boolean;
}

/**
 * Una linea de "como lo pagaste": un medio de pago, o el equipo que el cliente
 * dejo en parte de pago.
 *
 * Es la misma forma que devuelve `v_remito_publico_pagos` (migracion 0061), que
 * es de donde sale el mensaje cuando el remito se reenvia despues desde la
 * pagina. Aca se arma con lo que contesto el RPC al cerrar la venta: el mensaje
 * del panel y el de la pagina tienen que decir lo mismo.
 */
export interface LineaPagoRemito {
  clase: "PAGO" | "DEVOLUCION" | "ENTREGA";
  /** "Efectivo", "Transferencia", o el nombre del equipo entregado. */
  etiqueta: string;
  /** "Visa en 3 cuotas". Solo en las tarjetas. */
  detalle?: string | null;
  /** En la moneda con la que se pago de verdad, NUNCA convertido. */
  importe: number;
  moneda: Moneda;
  imei?: string | null;
}

export interface DatosRemito {
  numero: string;
  fecha: Date;
  moneda: Moneda;
  total: number;
  pagado: number;
  pendiente: number;
  items: ReadonlyArray<ItemRemito>;
  /** `null` en una venta de mostrador: el saludo pierde el nombre. */
  clienteNombre: string | null;
  negocio: string;
  url: string;
  /** Como se pago. Vacio en una venta a cuenta corriente: todavia no entro nada. */
  pagos: ReadonlyArray<LineaPagoRemito>;
  /**
   * La moneda de la cuenta del cliente y lo que quedo debiendo en ella, cuando
   * no es la de la venta. Mismo dato que `moneda_cuenta` / `pendiente_cuenta`
   * de `v_remito_publico` (0116): sin esto el panel mandaba «Queda un saldo de
   * US$ 12,58» a un cliente con la cuenta fija en pesos, y el remito reenviado
   * desde la pagina le decia ademas que quedaba fijo en $ 20.002,20.
   */
  monedaCuenta?: Moneda | null;
  pendienteCuenta?: number | null;
  /**
   * Cuanto de lo pagado fue el saldo a favor que ya tenia el cliente, en la
   * moneda de la venta (0121_8). Esta incluido en `pagado`, pero no es plata
   * que entrego hoy: el mensaje lo dice aparte, como la pagina del remito.
   */
  saldoAFavor?: number;
}

/**
 * Cuantos items entran antes de cortar.
 *
 * Un mensaje de WhatsApp que hay que scrollear deja de leerse. El resto esta
 * completo en la pagina del link, que es el documento de verdad.
 */
const TOPE_ITEMS = 6;

/** El primer nombre alcanza y suena a persona: "Hola Martín!" y no "Hola Martín Rodríguez!". */
function primerNombre(nombre: string): string {
  return nombre.trim().split(/\s+/)[0] ?? nombre;
}

function fechaCorta(d: Date): string {
  const p = (x: number): string => String(x).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * Si el desglose del pago DICE algo que "Pagaste $X" no dice.
 *
 * Mismo criterio que `precio_texto` en los items: repetir el mismo numero dos
 * veces no es mas informacion. Aporta cuando hay mas de una linea, cuando
 * alguna no es plata (el equipo entregado, una devolucion) o cuando la unica
 * linea esta en otra moneda que la venta.
 *
 * Gemela de `desglosaElPago()` en `backend/rutas/remito.ts`, por la misma razon
 * por la que el mensaje esta escrito dos veces: la pagina publica no carga el
 * bundle del SPA. Si una cambia, la otra tambien.
 */
function desglosaElPago(r: DatosRemito): boolean {
  if (r.pagos.length === 0) return false;
  if (r.pagos.length > 1) return true;
  if (r.pagos[0]!.clase !== "PAGO") return true;
  return r.pagos[0]!.moneda !== r.moneda;
}

/** El medio, cuando la venta se pago con uno solo y el desglose no va. */
function medioUnico(r: DatosRemito): string | null {
  if (r.pagos.length !== 1 || desglosaElPago(r)) return null;
  const p = r.pagos[0]!;
  return p.detalle ? `${p.etiqueta} · ${p.detalle}` : p.etiqueta;
}

/**
 * Como se lee una linea del desglose.
 *
 * El equipo entregado va en segunda persona —"Tu iPhone 12"— y no con el nombre
 * pelado: arriba esta la lista de lo que el cliente SE LLEVA, y sin esa palabra
 * las dos listas se leen igual.
 */
function rotuloPago(p: LineaPagoRemito): string {
  if (p.clase === "ENTREGA") return `Tu ${p.etiqueta}`;
  if (p.clase === "DEVOLUCION") return `Te devolvimos · ${p.etiqueta}`;
  return p.detalle ? `${p.etiqueta} · ${p.detalle}` : p.etiqueta;
}

/**
 * Cuando la deuda quedo en otra moneda que la de la venta, la frase que lo
 * dice; si no, null.
 *
 * Gemela de `deudaEnLaCuenta()` en `backend/rutas/remito.ts`: el cliente tiene
 * que recibir el mismo texto lo mande el panel o la pagina del remito.
 */
export function deudaEnLaCuenta(r: DatosRemito): string | null {
  const moneda = r.monedaCuenta;
  if (!moneda || moneda === r.moneda) return null;
  if (r.pendiente <= 0.009) return null;
  if (r.pendienteCuenta === null || r.pendienteCuenta === undefined) return null;
  const monto = formatear(r.pendienteCuenta, moneda);
  return moneda === "ARS"
    ? `Tu cuenta es en pesos: este saldo queda fijo en ${monto}, aunque cambie el dólar.`
    : `Tu cuenta es en dólares: este saldo queda en ${monto}.`;
}

export function armarMensajeRemito(r: DatosRemito): string {
  const saludo = r.clienteNombre
    ? `Hola ${primerNombre(r.clienteNombre)}! Acá está tu remito de ${r.negocio}.`
    : `Hola! Acá está tu remito de ${r.negocio}.`;

  const visibles = r.items.slice(0, TOPE_ITEMS);
  const sobran = r.items.length - visibles.length;

  const lineas = visibles.map(
    (i) =>
      `${i.cantidad}× ${i.descripcion} — ${formatear(i.importe, r.moneda)}` +
      (i.a_pedido ? " (a pedido: se entrega cuando llegue)" : ""),
  );
  if (sobran > 0) {
    lineas.push(sobran === 1 ? "y 1 ítem más" : `y ${sobran} ítems más`);
  }

  // Cada linea en SU moneda: es la plata que la persona entrego.
  const desglose = desglosaElPago(r)
    ? [
        "",
        "Cómo lo pagaste:",
        // La devolucion en positivo: el rotulo ya dice que la plata salio.
        ...r.pagos.map(
          (p) =>
            `${rotuloPago(p)} — ${formatear(p.clase === "DEVOLUCION" ? Math.abs(p.importe) : p.importe, p.moneda)}`,
        ),
      ]
    : [];

  const medio = medioUnico(r);
  // El saldo a favor usado no es plata que entrego hoy: va en su linea, como
  // en la pagina del remito (backend `canceladoCon`).
  const aFavor = Math.min(Math.max(r.saldoAFavor ?? 0, 0), Math.max(r.pagado, 0));
  const plata = Math.round((r.pagado - aFavor) * 100) / 100;
  const pagado = medio ? `${formatear(plata, r.moneda)} · ${medio}` : formatear(plata, r.moneda);
  const conSaldo =
    aFavor > 0.009 ? `Usaste ${formatear(aFavor, r.moneda)} de tu saldo a favor.` : null;
  // Una venta a cuenta no tiene «Entregaste $ 0,00»: no entrego nada, todo
  // quedo en su cuenta.
  const saldo = `Queda un saldo de ${formatear(r.pendiente, r.moneda)} en tu cuenta.`;
  const pago = [
    r.pendiente > 0.009
      ? plata > 0.009
        ? `Entregaste ${pagado}.`
        : null
      : plata > 0.009 || conSaldo === null
        ? `Pagaste ${pagado}.`
        : null,
    conSaldo,
    r.pendiente > 0.009 ? saldo : null,
  ]
    .filter((l): l is string => l !== null)
    .join("\n");
  // En su propio renglon, debajo del saldo, como en el backend.
  const deuda = deudaEnLaCuenta(r);

  return [
    saludo,
    "",
    `${r.numero} · ${fechaCorta(r.fecha)}`,
    ...lineas,
    "",
    `TOTAL ${formatear(r.total, r.moneda)}`,
    ...desglose,
    pago,
    ...(deuda ? [deuda] : []),
    "",
    "Verlo y guardarlo:",
    r.url,
  ].join("\n");
}

/** La URL publica del remito. La base la sirve el backend en `/config`. */
export function urlRemito(base: string, token: string): string {
  return `${base.replace(/\/+$/, "")}/r/${token}`;
}
